import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Small status pill. Introduced for the Story 16.2 "Account disabled · pending
// verification" topbar chip; kept generic (variant-driven, token-only) so other
// status chips can reuse it. Always render explicit text inside — never rely on
// colour alone to convey state (WCAG: "more than colour").
//
// Story 6.9 adds the canonical evaluation-status + subscription variants. The
// label↔variant↔state map (info=Draft, pending=Ready to score, ok=Completed) lives in
// `lib/evaluation-status.ts`; the B2C subscription pill uses `warning`=Subscription
// lapsed (amber, to match its Banner variant="warn" and stay below clinical-alarm —
// Story 10.5's surface). Story 13.6 narrows that decision to B2C: an ORG contract
// lapse is a hard, settled block (renewal is org-admin/CITS scope, not the user's),
// so the org pill uses `blocked`="Lapsed — read-only" per UX 13.11. This file
// owns only the token styling. Each variant pairs a desaturated clinical background with
// its matching Banner text token so a pill and a banner of the same state agree.
const badgeVariants = cva(
  "inline-flex items-center rounded-[var(--radius-full)] border px-3 py-1 text-xs font-semibold",
  {
    variants: {
      variant: {
        // Muted warning (desaturated, not clinical-alarm) — matches the design
        // system's warning tokens used by Banner variant="warn".
        warning:
          "border-[color:var(--bg-warning-secondary)] bg-[color:var(--bg-warning-primary)] text-[color:var(--text-warning)]",
        // Neutral brand pill, mirroring the existing RolePill styling.
        neutral:
          "border-[color:var(--border-brand-alt)] bg-[color:var(--bg-brand-primary)] text-[color:var(--text-brand-secondary)]",
        // info = "Draft" (draft) — brand-soft, the same pairing as Banner info.
        info:
          "border-[color:var(--bg-brand-secondary)] bg-[color:var(--bg-brand-primary)] text-[color:var(--text-brand-secondary)]",
        // pending = "Ready to score" (submitted), and every waiting subscription
        // state — "Activating after calibration" plus Story 16.7's two
        // legacy-onboarding rows — muted warning (alias of `warning`, named for
        // the status semantics so call-sites read intent-first). The subscription
        // strings live in `SUBSCRIPTION_PILL` (`lib/subscription-copy.ts`) and
        // are deliberately not repeated here; this file owns only the tokens.
        pending:
          "border-[color:var(--bg-warning-secondary)] bg-[color:var(--bg-warning-primary)] text-[color:var(--text-warning)]",
        // ok = "Completed" (scored) / "Subscription active" — desaturated
        // success. Story 16.6 briefly gave the Profile card a bare "Active"; the
        // PM ruling of 2026-08-31 reverted that, so the topbar chip and the card
        // now share this variant AND their copy (one mapper owns both).
        ok:
          "border-[color:var(--bg-success-secondary)] bg-[color:var(--bg-success-primary)] text-[color:var(--text-success)]",
        // blocked = a hard, settled block state — desaturated error (paired with Banner
        // danger; still text-labelled, never colour-only). The B2C subscription pill
        // keeps the muted `warning` token (no clinical-alarm red); the ORG-lapsed pill
        // uses THIS variant ("Lapsed — read-only", Story 13.6 / UX 13.11).
        blocked:
          "border-[color:var(--border-error)] bg-[color:var(--bg-error-primary)] text-[color:var(--text-error)]",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />
  ),
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
